import { useState, useCallback } from 'react';

/**
 * A validation rule receives the field value and the entire form,
 * and returns an error string if invalid, or undefined if valid.
 */
type ValidationRule<TForm, TValue> = (value: TValue, form: TForm) => string | undefined;

/**
 * Map of field names to their validation rules.
 * Only fields that need validation need entries.
 */
type ValidationRules<TForm> = {
  [K in keyof TForm]?: ValidationRule<TForm, TForm[K]>;
};

interface UseFormValidationResult<TForm> {
  /** Current field-level error messages. Keys match field names from TForm. */
  errors: Partial<Record<keyof TForm, string>>;
  /** Run all rules against the current values. Returns true if no errors. */
  validate: (values: TForm) => boolean;
  /** Clear a single field error (e.g., on input change). */
  clearFieldError: (field: keyof TForm) => void;
  /** Clear all errors at once. */
  clearErrors: () => void;
  /** Manually set errors (e.g., from server-side validation). */
  setErrors: (errors: Partial<Record<keyof TForm, string>>) => void;
}

/**
 * Reusable form validation hook.
 *
 * Usage:
 * ```ts
 * const { errors, validate, clearFieldError } = useFormValidation<MyForm>({
 *   firstName: (v) => (!v.trim() ? 'First name is required' : undefined),
 *   email: (v) => (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'Invalid email' : undefined),
 * });
 *
 * const handleSubmit = () => {
 *   if (!validate(formData)) return;
 *   // proceed
 * };
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useFormValidation<TForm extends Record<string, any>>(
  rules: ValidationRules<TForm>,
): UseFormValidationResult<TForm> {
  const [errors, setErrors] = useState<Partial<Record<keyof TForm, string>>>({});

  const validate = useCallback(
    (values: TForm): boolean => {
      const newErrors: Partial<Record<keyof TForm, string>> = {};
      for (const [key, rule] of Object.entries(rules)) {
        if (typeof rule === 'function') {
          const error = (rule as ValidationRule<TForm, TForm[keyof TForm]>)(
            values[key as keyof TForm],
            values,
          );
          if (error) {
            newErrors[key as keyof TForm] = error;
          }
        }
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [rules],
  );

  const clearFieldError = useCallback((field: keyof TForm) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const clearErrors = useCallback(() => setErrors({}), []);

  return { errors, validate, clearFieldError, clearErrors, setErrors };
}
